// frontend_react/src/components/AdminPanel/CreateCompanyPanel.jsx

import React, { useState } from "react";
import styled from "styled-components";
import { AdminButton } from "../../pages/admin/action_engine";
import { createCompany } from "../../services/adminApi";
import logger from "../../utils/logger";

import {
  AdminFormSection,
  AdminSectionBadge,
  AdminSectionHeader,
} from "../../pages/admin/section_engine";

export default function CreateCompanyPanel({ onCreated }) {
  const emptyForm = {
    company_type: "operator",

    name: "",
    legal_name: "",
    commercial_name: "",

    siren: "",
    siret: "",
    tva_number: "",
    naf_code: "",

    legal_form: "",
    share_capital: "",
    rcs_city: "",
    rcs_number: "",

    email: "",
    phone: "",
    website_url: "",

    address_line1: "",
    address_line2: "",
    postal_code: "",
    city: "",
    country: "France",

    logo_url: "",
    receipt_legal_notice: "",
    invoice_legal_notice: "",
  };

  const [formData, setFormData] = useState(emptyForm);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const requiredFields = [
      "company_type",
      "name",
      "address_line1",
      "postal_code",
      "city",
      "country",
    ];

    for (const field of requiredFields) {
      if (!formData[field]?.toString().trim()) {
        setError(`Le champ “${field}” est obligatoire.`);
        return false;
      }
    }

    return true;
  };

  const resetForm = () => {
    setFormData(emptyForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");

    if (!validate()) return;

    try {
      await createCompany({
        ...formData,
        share_capital: formData.share_capital
          ? Number(formData.share_capital)
          : null,
      });

      setMessage("Société créée avec succès.");
      resetForm();

      if (onCreated) onCreated();
    } catch (err) {
      logger.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.msg ||
          "Erreur lors de la création de la société."
      );
    }
  };

  return (
    <FormWrapper onSubmit={handleSubmit}>
      {message && <PanelMessage>{message}</PanelMessage>}
      {error && <PanelMessage $error>{error}</PanelMessage>}

      <AdminFormSection>
        <AdminSectionHeader>
          <AdminSectionBadge>Société</AdminSectionBadge>
          <h4>Informations générales</h4>
        </AdminSectionHeader>

        <FieldsGrid>
          <Field $full>
            <label>Type de société *</label>
            <select
              name="company_type"
              value={formData.company_type}
              onChange={handleChange}
              required
            >
              <option value="operator">Exploitant Queen’s 360</option>
              <option value="supplier">Fournisseur</option>
              <option value="customer">Client professionnel</option>
              <option value="partner">Partenaire</option>
              <option value="holding">Holding / société du groupe</option>
            </select>
          </Field>

          <Field $full>
            <label>Nom *</label>
            <input name="name" value={formData.name} onChange={handleChange} required />
          </Field>

          <Field $full>
            <label>Raison sociale</label>
            <input
              name="legal_name"
              value={formData.legal_name}
              onChange={handleChange}
              placeholder="Ex : MAAC GAME"
            />
          </Field>

          <Field $full>
            <label>Nom commercial</label>
            <input
              name="commercial_name"
              value={formData.commercial_name}
              onChange={handleChange}
              placeholder="Ex : Queen's Games"
            />
          </Field>
        </FieldsGrid>
      </AdminFormSection>

      <AdminFormSection>
        <AdminSectionHeader>
          <AdminSectionBadge>Légal</AdminSectionBadge>
          <h4>Immatriculation & mentions</h4>
        </AdminSectionHeader>

        <FieldsGrid>
          <Field>
            <label>SIREN</label>
            <input name="siren" value={formData.siren} onChange={handleChange} />
          </Field>

          <Field>
            <label>SIRET</label>
            <input name="siret" value={formData.siret} onChange={handleChange} />
          </Field>

          <Field>
            <label>N° TVA Intracommunautaire</label>
            <input name="tva_number" value={formData.tva_number} onChange={handleChange} />
          </Field>

          <Field>
            <label>Code NAF / APE</label>
            <input name="naf_code" value={formData.naf_code} onChange={handleChange} />
          </Field>

          <Field>
            <label>Forme juridique</label>
            <input
              name="legal_form"
              value={formData.legal_form}
              onChange={handleChange}
              placeholder="SAS, SARL..."
            />
          </Field>

          <Field>
            <label>Capital social</label>
            <input
              type="number"
              step="0.01"
              name="share_capital"
              value={formData.share_capital}
              onChange={handleChange}
            />
          </Field>

          <Field>
            <label>Ville RCS</label>
            <input name="rcs_city" value={formData.rcs_city} onChange={handleChange} />
          </Field>

          <Field>
            <label>N° RCS</label>
            <input name="rcs_number" value={formData.rcs_number} onChange={handleChange} />
          </Field>
        </FieldsGrid>
      </AdminFormSection>

      <AdminFormSection>
        <AdminSectionHeader>
          <AdminSectionBadge>Contact</AdminSectionBadge>
          <h4>Email, téléphone & web</h4>
        </AdminSectionHeader>

        <FieldsGrid>
          <Field>
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
          </Field>

          <Field>
            <label>Téléphone</label>
            <input name="phone" value={formData.phone} onChange={handleChange} />
          </Field>

          <Field $full>
            <label>Site web</label>
            <input
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              placeholder="https://..."
            />
          </Field>

          <Field $full>
            <label>Logo URL</label>
            <input
              name="logo_url"
              value={formData.logo_url}
              onChange={handleChange}
              placeholder="/uploads/logos/..."
            />
          </Field>
        </FieldsGrid>
      </AdminFormSection>

      <AdminFormSection>
        <AdminSectionHeader>
          <AdminSectionBadge>Adresse</AdminSectionBadge>
          <h4>Coordonnées postales</h4>
        </AdminSectionHeader>

        <FieldsGrid>
          <Field $full>
            <label>Adresse ligne 1 *</label>
            <input
              name="address_line1"
              value={formData.address_line1}
              onChange={handleChange}
              required
            />
          </Field>

          <Field $full>
            <label>Adresse ligne 2</label>
            <input
              name="address_line2"
              value={formData.address_line2}
              onChange={handleChange}
            />
          </Field>

          <Field>
            <label>Code postal *</label>
            <input
              name="postal_code"
              value={formData.postal_code}
              onChange={handleChange}
              required
            />
          </Field>

          <Field>
            <label>Ville *</label>
            <input name="city" value={formData.city} onChange={handleChange} required />
          </Field>

          <Field>
            <label>Pays *</label>
            <input
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
            />
          </Field>
        </FieldsGrid>
      </AdminFormSection>

      <AdminFormSection>
        <AdminSectionHeader>
          <AdminSectionBadge>PDF</AdminSectionBadge>
          <h4>Mentions ticket & facture</h4>
        </AdminSectionHeader>

        <FieldsGrid>
          <Field $full>
            <label>Mention légale ticket</label>
            <textarea
              name="receipt_legal_notice"
              value={formData.receipt_legal_notice}
              onChange={handleChange}
              placeholder="Ex : Ticket à conserver. Aucun remboursement sans justificatif."
            />
          </Field>

          <Field $full>
            <label>Mention légale facture</label>
            <textarea
              name="invoice_legal_notice"
              value={formData.invoice_legal_notice}
              onChange={handleChange}
              placeholder="Ex : TVA acquittée sur les encaissements..."
            />
          </Field>
        </FieldsGrid>
      </AdminFormSection>

      <AdminButton type="submit" variant="success" fullWidth>Créer la société</AdminButton>
    </FormWrapper>
  );
}

const FormWrapper = styled.form`
  display: grid;
  gap: 0.95rem;
  color: #f7f7f7;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
`;

const Field = styled.div`
  display: grid;
  gap: 0.35rem;
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};

  label {
    font-size: 0.78rem;
    font-weight: 850;
    opacity: 0.82;
  }

  input,
  select,
  textarea {
    width: 100%;
    min-height: 42px;
    border: none;
    border-radius: 10px;
    padding: 0.75rem;
    background: #fff9ea;
    color: #0b1328;
    font-weight: 750;
    box-sizing: border-box;
    outline: none;
  }

  input:focus,
  select:focus,
  textarea:focus {
    box-shadow: 0 0 0 3px rgba(255, 249, 234, 0.22);
  }

  textarea {
    resize: vertical;
    min-height: 110px;
  }
`;

const PanelMessage = styled.div`
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;